import api, { ResponseCallback } from '../services/api.service'
import routeUtil from '../utils/route.util'
import { PermissionData } from './permission.api'
import { SiteData } from './site.api'
import allRoutes from '../routes/routes'

export interface UserData {
    id: number
    siteId: number
    createdBy?: number
    updatedBy?: number
    blockedBy?: number
    username: string
    email?: string
    phone?: string
    timeout: number
    blockedAt?: string
    createdAt: string
    updatedAt: string
    isBlocked: boolean
    site?: SiteData
    permissions?: PermissionData[]
    createdUser?: UserData
    updatedUser?: UserData
    isLabelBuilder: boolean
}

export interface UserSignInRequest {
    username?: string
    password?: string
}

interface UserSignInResponse {
    token: string
    isLabelUser: boolean
    inTrial: boolean
    plan: string
    userCount: number
}

export interface UserResetPasswordRequest {
    email?: string
}

interface UserResetPasswordResponse {
    sent: boolean
}

export interface UserUpdatePasswordRequest {
    token?: string
    newPassword?: string
    confirmPassword?: string
}

interface UserUpdatePasswordResponse {
    updated: boolean
}

interface UserGenerateTokenResponse {
    token: string
}

interface UserHasPermissionResponse {
    has: boolean
    redirectModule: keyof typeof allRoutes
}

export interface UserCreateRequest {
    username?: string
    site?: number
    password?: string
    email?: string
    phone?: string
    notify?: boolean
    timeout?: number
    permissions?: number[]
}

export interface UserUpdateRequest {
    username?: string
    password?: string
    email?: string
    phone?: string
    notify?: boolean
    timeout?: number
    permissions?: number[]
}

const routes = {
  signIn: '/users/signIn',
  resetPassword: '/users/password/reset',
  updatePassword: '/users/password/update',
  read: '/users/{id}',
  generateToken: '/users/{id}/token/generate',
  readSite: '/users/{id}/site',
  list: '/users/lists',
  block: '/users/{id}/block',
  unblock: '/users/{id}/unblock',
  create: '/users',
  update: '/users/{id}',
  hasPermission: '/users/{id}/permissions/{moduleName}',
  userSearch: '/users/search'
}

export default {
  signIn (
    payload: UserSignInRequest,
    callback: ResponseCallback<UserSignInResponse>
  ) {
    api.post<UserSignInRequest, UserSignInResponse>(
      routes.signIn,
      payload,
      callback
    )
  },

  resetPassword (
    payload: UserResetPasswordRequest,
    callback: ResponseCallback<UserResetPasswordResponse>
  ) {
    api.post<UserResetPasswordRequest, UserResetPasswordResponse>(
      routes.resetPassword,
      payload,
      callback
    )
  },

  updatePassword (
    payload: UserUpdatePasswordRequest,
    callback: ResponseCallback<UserUpdatePasswordResponse>
  ) {
    api.post<UserUpdatePasswordRequest, UserUpdatePasswordResponse>(
      routes.updatePassword,
      payload,
      callback
    )
  },

  read (id: number, callback: ResponseCallback<UserData>) {
    const url = routeUtil.replaceParams(routes.read, { id })
    api.get(url, {}, callback)
  },

  generateToken (id: number, callback: ResponseCallback<UserGenerateTokenResponse>) {
    const url = routeUtil.replaceParams(routes.generateToken, { id })
    api.get(url, {}, callback)
  },

  readSite (id: number, callback: ResponseCallback<SiteData>) {
    const url = routeUtil.replaceParams(routes.readSite, { id })
    api.get(url, {}, callback)
  },

  list (
    search: string = '',
    callback: ResponseCallback<UserData[]>
  ) {
    api.post(routes.list, { search }, callback)
  },

  block (id: number, callback: ResponseCallback<UserData>) {
    const url = routeUtil.replaceParams(routes.block, { id })
    api.patch(url, {}, callback)
  },

  unblock (id: number, callback: ResponseCallback<UserData>) {
    const url = routeUtil.replaceParams(routes.unblock, { id })
    api.patch(url, {}, callback)
  },

  create (payload: UserCreateRequest, callback: ResponseCallback<UserData>) {
    api.post(routes.create, payload, callback)
  },

  update (
    id: number,
    payload: UserUpdateRequest,
    callback: ResponseCallback<UserData>
  ) {
    const url = routeUtil.replaceParams(routes.update, { id })
    api.patch(url, payload, callback)
  },

  hasPermission (
    id: number,
    moduleName: string,
    callback: ResponseCallback<UserHasPermissionResponse>
  ) {
    const params = { id, moduleName }
    const url = routeUtil.replaceParams(routes.hasPermission, params)
    api.get(url, {}, callback)
  }
}
